import { OwnerExchangeCardJsonldOwnerExchangeRequestReadOwnerExchangeRequestDetailRead } from '@/api/generated';
import { yupResolver } from '@hookform/resolvers/yup';
import { useModal } from 'context/Modal/Context';
import { MODAL_NAME } from 'context/Modal/MODAL_NAME';
import { UserTradeListFilters, useUserTradeList } from 'hooks/queries/tradeList';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type FormValues = Record<string, number>;
type Payload = Array<{ card: string; quantity: number }>;
type Props = {
  user: { nickName: string; id: string };
  onSubmit: (p: Payload) => void;
  defaultValues?: OwnerExchangeCardJsonldOwnerExchangeRequestReadOwnerExchangeRequestDetailRead[];
};

const schema = () =>
  yup
    .object()
    .test(
      'at-least-one-field',
      'no-cards-to-exchange',
      (value: FormValues) => Object.values(value).reduce((acc, val) => acc + (val || 0), 0) !== 0,
    );

const useCardTradeListModal = ({ user, onSubmit, defaultValues }: Props) => {
  const { closeModal } = useModal();
  const [filters, setFilters] = useState<Partial<UserTradeListFilters>>({});

  const { data } = useUserTradeList({ userId: user.id, filters });

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: yupResolver(schema()),
  });

  const onFilterSubmit = (values: Partial<UserTradeListFilters>) => {
    setFilters(values);
    closeModal(MODAL_NAME.FILTERS);
  };

  const handleSubmit = (v: FormValues) => {
    const formatted = Object.keys(v).reduce(
      (acc: Payload, cardIri) => [
        ...acc,
        {
          card: cardIri,
          quantity: v[cardIri],
        },
      ],
      [],
    );
    onSubmit(formatted);
  };

  const onClear = () => setFilters({});

  useEffect(() => {
    if (defaultValues) {
      const formattedValues = defaultValues.reduce((acc, exchange) => {
        return { ...acc, [exchange.card?.['@id'] as string]: exchange.quantity };
      }, {}) as FormValues;

      formMethods.reset(formattedValues);
    }
  }, [formMethods, defaultValues]);

  return {
    data,
    onFilterSubmit,
    formMethods,
    handleSubmit: formMethods.handleSubmit(handleSubmit),
    filters,
    setFilters,
    onClear,
  };
};

export default useCardTradeListModal;
